// src/pages/ProjectDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import projectsData from '../data/projects.json';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [content, setContent] = useState('');
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProject = async () => {
      setLoading(true);
      
      try {
        // Find project metadata
        const proj = projectsData.find(p => p.id === id);
        if (!proj) {
          setLoading(false);
          return;
        }

        setProject(proj);

        // Load markdown content
        try {
          const response = await fetch(`${process.env.PUBLIC_URL}/projects/content/${id}.md`);
          if (!response.ok) {
            throw new Error(`Failed to load project content: ${response.status}`);
          }
          const text = await response.text();
          setContent(text);
        } catch (error) {
          console.error('Error loading project content:', error);
          setContent(`# ${proj.title}\n\n${proj.excerpt}\n\n## Achievements\n\n${proj.achievements.map(a => `- ${a}`).join('\n')}`);
        }

        // Find related projects (same category)
        const related = projectsData
          .filter(p => p.id !== id && p.category === proj.category)
          .slice(0, 3);
        setRelatedProjects(related);
      } catch (error) {
        console.error('Error loading project:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [id]);

  if (loading) {
    return (
      <div className="full-section">
        <div className="loading">Loading project details...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="full-section">
        <div className="not-found">
          <h1>Project not found</h1>
          <Link to="/projects" className="btn-primary">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <section className="full-section project-detail">
      <div className="project-header">
        <Link to="/projects" className="back-link">
          ← Back to Projects
        </Link>
        
        <div className="project-meta-header">
          <div className="project-category-tag">{project.category}</div>
          <div className="project-date">{project.date}</div>
        </div>

        <h1>{project.title}</h1>
        <p className="project-excerpt">{project.excerpt}</p>
        
        <div className="project-info">
          <div className="info-item">
            <span className="info-label">Role:</span>
            <span className="info-value">{project.role}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Company:</span>
            <span className="info-value">{project.company}</span>
          </div>
        </div>

        <div className="project-stats">
          <div className="project-tech">
            <h4>Technologies:</h4>
            <div className="tech-tags">
              {project.tech.map(tech => (
                <span key={tech} className="tech-tag">{tech}</span>
              ))}
            </div>
          </div>
          <div className="project-tags">
            <h4>Tags:</h4>
            <div className="tags">
              {project.tags.map(tag => (
                <span key={tag} className="tag">{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="project-content-container">
        <div className="project-image-large">
          <div className="image-placeholder-large">
            <div className="image-text">{project.title}</div>
          </div>
        </div>

        <article className="markdown-content">
          <ReactMarkdown>{content}</ReactMarkdown>
        </article>

        <div className="achievements-section">
          <h3>Key Achievements</h3>
          <ul className="achievements-list">
            {project.achievements.map((achievement, index) => (
              <li key={index}>{achievement}</li>
            ))}
          </ul>
        </div>

        {relatedProjects.length > 0 && (
          <div className="related-projects">
            <h3>Related Projects</h3>
            <div className="related-grid">
              {relatedProjects.map(proj => (
                <Link key={proj.id} to={`/projects/${proj.id}`} className="related-card">
                  <h4>{proj.title}</h4>
                  <p>{proj.excerpt}</p>
                  <div className="related-meta">
                    <span>{proj.category}</span>
                    <span>•</span>
                    <span>{proj.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProjectDetail;